import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { AdItem } from '@sbt-web/network/types';
import { ItemPicture, ItemPrice } from '@sbt-web/ui';
import { AdItemCardMini } from '@sbt-web/item-card';
import { trackCardClick, trackCardImpression } from '@/client/components/Adv/Geoboost/tracking';
import { getTrackingItem } from '@/tools/tracking/adsListingImpressionsTracking/buildCardImpressionTrackingData';
import { useTrackImpression } from '@/tools/tracking/useTrackImpression';
import styles from './index.module.scss';
type CardProps = {
  ad: AdItem;
  rank: number;
  searchId: string;
  className?: string;
  isMobile?: boolean;
};
const Card = t0 => {
  const $ = _c(14);
  const {
    ad,
    rank,
    searchId,
    className,
    isMobile
  } = t0;
  let t1;
  if ($[0] !== ad || $[1] !== rank || $[2] !== searchId) {
    t1 = getTrackingItem(ad, "geoboost", searchId, rank);
    $[0] = ad;
    $[1] = rank;
    $[2] = searchId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const trackingItem = t1;
  let t2;
  if ($[4] !== trackingItem) {
    t2 = () => trackCardImpression(trackingItem);
    $[4] = trackingItem;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const trackOnImpression = t2;
  let t3;
  if ($[6] !== trackingItem) {
    t3 = () => trackCardClick(trackingItem);
    $[6] = trackingItem;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleOnClick = t3;
  let t4;
  if ($[8] !== ad || $[9] !== className || $[10] !== handleOnClick || $[11] !== isMobile || $[12] !== trackOnImpression) {
    t4 = <DefaultCard ad={ad} className={className} trackOnImpression={trackOnImpression} handleOnClick={handleOnClick} isMobile={isMobile} />;
    $[8] = ad;
    $[9] = className;
    $[10] = handleOnClick;
    $[11] = isMobile;
    $[12] = trackOnImpression;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
type DefaultCardProps = {
  ad: AdItem;
  className?: string;
  trackOnImpression: () => void;
  handleOnClick: () => void;
  isMobile?: boolean;
};
const DefaultCard = t0 => {
  const $ = _c(28);
  const {
    ad,
    className,
    trackOnImpression,
    handleOnClick,
    isMobile
  } = t0;
  const componentRef = useRef(null);
  let t1;
  if ($[0] !== trackOnImpression) {
    t1 = {
      enabled: true,
      track: trackOnImpression
    };
    $[0] = trackOnImpression;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setComponentRef = useTrackImpression(t1);
  let t2;
  let t3;
  if ($[2] !== setComponentRef) {
    t2 = () => {
      if (componentRef?.current) {
        setComponentRef(componentRef.current);
      }
    };
    t3 = [setComponentRef, componentRef];
    $[2] = setComponentRef;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (isMobile) {
    let t4;
    if ($[5] !== ad || $[6] !== handleOnClick) {
      t4 = <AdItemCardMini ref={componentRef} assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL} item={ad} onClick={handleOnClick} aspectRatio="1" imageSizeType="fixed" disableFavourite={true} />;
      $[5] = ad;
      $[6] = handleOnClick;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  const t4 = `${styles.card} ${className}`;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["108x82", "160x120", "120x120", "121x82"];
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== ad.category.id || $[10] !== ad.images || $[11] !== ad.subject) {
    t6 = <ItemPicture subject={ad.subject} categoryID={ad.category.id} images={ad.images} formats={t5} className={styles.thumbnail} />;
    $[9] = ad.category.id;
    $[10] = ad.images;
    $[11] = ad.subject;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== ad.subject) {
    t7 = <h6 className={`body-text small semibold ${styles.subject}`}>{ad.subject}</h6>;
    $[13] = ad.subject;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = ad.advertiser.shopName ?? ad.advertiser.name;
  let t9;
  if ($[15] !== t8) {
    t9 = <span className={`caption small ${styles.seller}`}>{t8}</span>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== ad.features || $[18] !== ad.type) {
    t10 = ad.features["/price"] && <ItemPrice classes={[styles.price]} adType={ad.type.key} price={Number(ad.features["/price"].values[0].key)} />;
    $[17] = ad.features;
    $[18] = ad.type;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== ad.urls.default || $[21] !== handleOnClick || $[22] !== t10 || $[23] !== t4 || $[24] !== t6 || $[25] !== t7 || $[26] !== t9) {
    t11 = <a href={ad.urls.default} className={t4} ref={componentRef} onClick={handleOnClick}>{t6}{t7}{t9}{t10}</a>;
    $[20] = ad.urls.default;
    $[21] = handleOnClick;
    $[22] = t10;
    $[23] = t4;
    $[24] = t6;
    $[25] = t7;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
export { Card };